import { useMemo, useState, type ReactNode } from 'react';
import { CaretDownIcon, CaretRightIcon, PlusIcon } from '@phosphor-icons/react';
import { Num, T, Var } from 'gt-react';
import { executionRootId } from '@erp/shared-types';
import {
  processOutline,
  type OutlineCell,
  type OutlineInstruction,
} from '@erp/workbooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { WorkbookEditor } from './useWorkbookEditor';
import {
  WorkbookProcessContext,
  useWorkbookProcessState,
} from './useWorkbookProcess';
import { WorkbookCellInstructions } from './WorkbookProcessCell';
import { WorkbookRegenerateDialog } from './WorkbookGeneration';
import { WorkbookRegenerateSopButton } from './WorkbookSopUpdateDialog';
import {
  useInstructionReviewChecks,
  type InstructionReviewCheck,
} from './useInstructionReviewChecks';

/**
 * An Item's SOP in its detail tab: the Instructions whose Linked items include
 * it, collapsed until opened and edited in place with the Process's editor. An
 * Instruction whose Linked items changed is marked Stale, and the Match check
 * confirms it while the tab is on screen.
 */
export function WorkbookItemInstructions({
  editor,
  itemId,
  canUpdate,
  active,
  openItem,
  renderStepActions,
}: {
  editor: Pick<WorkbookEditor, 'definition' | 'edits' | 'workbookId'>;
  /** The integrations' actions on an Instruction. */
  renderStepActions?: (stepId: string) => ReactNode;
  itemId: string;
  canUpdate: boolean;
  /** This tab is on screen: only it runs the Match check. */
  active: boolean;
  openItem: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const { definition, edits, workbookId } = editor;
  const linked = useMemo(() => {
    const outline = processOutline(definition);
    return outline.nodes.filter(
      (node): node is OutlineInstruction =>
        node.kind === 'instruction' &&
        outline.linkedItemIds(node.id).has(itemId),
    );
  }, [definition, itemId]);
  const checks = useInstructionReviewChecks(
    workbookId,
    definition,
    linked.map((node) => node.id),
    active,
  );
  const state = useWorkbookProcessState({
    definition,
    edits,
    canUpdate,
    workbookId,
    openItem,
    reviewCheck: (stepId) => {
      const check = checks.get(stepId);
      return check && <WorkbookReviewCheckText check={check} />;
    },
    renderStepActions,
  });
  const { outline } = state;
  const stale = [...checks.values()].filter(
    (check) => check.state !== 'matches',
  ).length;
  const [first, ...rest] = linked;
  const cell: OutlineCell | undefined = first && {
    head: first,
    body: rest,
    instructions: linked,
  };
  const isSheet = definition.sheets.some((sheet) => sheet.id === itemId);
  function add() {
    if (!isSheet) return state.insert(null, undefined, [itemId]);
    const root = executionRootId(itemId);
    state.insert(root, outline.children(root).at(-1)?.id);
  }
  return (
    <WorkbookProcessContext.Provider value={state}>
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="rounded-lg border border-input bg-card"
      >
        <div className="flex items-center gap-2 pr-2">
          <h3 className="min-w-0 flex-1">
            <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm font-medium hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring">
              {open ? (
                <CaretDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <CaretRightIcon className="size-4 text-muted-foreground" />
              )}
              <T context="workbook item sop heading">Instructions (SOP)</T>
              <span className="text-xs font-normal text-muted-foreground">
                <Num>{linked.length}</Num>
              </span>
              {stale > 0 && (
                <Badge variant="outline" className="text-xs font-normal">
                  <T context="workbook item sop stale count">
                    <Num>{stale}</Num> stale
                  </T>
                </Badge>
              )}
            </CollapsibleTrigger>
          </h3>
          {canUpdate && (
            <WorkbookRegenerateSopButton
              stepIds={linked.map((node) => node.id)}
              whole
              variant="ghost"
              disabled={state.generation.busy}
            />
          )}
        </div>
        <CollapsibleContent>
          <div className="flex flex-col gap-3 border-t border-input p-3">
            {cell ? (
              <WorkbookCellInstructions cell={cell} showHeadTitle standalone />
            ) : (
              <p className="text-xs text-muted-foreground">
                <T context="workbook item sop empty">
                  No instructions describe this yet.
                </T>
              </p>
            )}
            {canUpdate && !outline.full && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 self-start"
                disabled={state.generation.generating}
                onClick={add}
              >
                <PlusIcon />
                <T context="workbook add process instruction">
                  Add instruction
                </T>
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
      <WorkbookRegenerateDialog />
    </WorkbookProcessContext.Provider>
  );
}

/** The Match check's verdict on an Instruction whose Linked items changed. */
function WorkbookReviewCheckText({ check }: { check: InstructionReviewCheck }) {
  return (
    <span className="mt-1 block text-xs">
      {check.state === 'checking' ? (
        <T context="workbook instruction review check">
          Checking whether it still matches…
        </T>
      ) : check.state === 'unavailable' ? null : check.state === 'stale' ? (
        <T context="workbook instruction review check">
          Stale: the AI check finds the settings no longer do what it says (
          <Var>{Math.round(check.probability * 100)}</Var>% match).
        </T>
      ) : (
        <T context="workbook instruction review check">
          The AI check finds it still matches the settings (
          <Var>{Math.round(check.probability * 100)}</Var>% match).
        </T>
      )}
    </span>
  );
}
